import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Organization, OrganizationConfiguration } from '@robocorp/robocloud-internal-cds-sdk';
import { RenderMode } from '~/types/common';
import { useParams } from '~/hooks';
import { organizationService as userOrganizationService } from '../../services/userAPI';
import { organizationService } from '../../services/organizationAPI';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  UseSafeApiQueryResult,
  validateApiResponse,
} from '../utils';

export enum OrganizatioinsQueryErrors {
  GET_ORGANIZATIONS_ERROR = 'GET_ORGANIZATIONS_ERROR',
  CREATE_ORGANIZATION_ERROR = 'CREATE_ORGANIZATION_ERROR',
  EDIT_ORGANIZATION_ERROR = 'EDIT_ORGANIZATION_ERROR',
  DELETE_ORGANIZATION_ERROR = 'DELETE_ORGANIZATION_ERROR',
  LEAVE_ORGANIZATION_ERROR = 'LEAVE_ORGANIZATION_ERROR',
  GET_ORGANIZATION_BY_SHORT_NAME_ERROR = 'GET_ORGANIZATION_BY_SHORT_NAME_ERROR',
}

export const getOrganizationsQueryKey = () => ['organizations'];
export const getOrganizationsByShortNameQueryKey = () => ['organizationsorganizationByShortName'];
const getOrganizationByShortNameQueryKey = (args: {
  organizationShortName: string | undefined;
}) => [...getOrganizationsByShortNameQueryKey(), args];

export function useOrganizationsQuery(
  options?: UseApiQueryOptions<typeof userOrganizationService.getAll>,
) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useQuery(
    getOrganizationsQueryKey(),
    async () => {
      const response = validateApiResponse(await userOrganizationService.getAll());
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizatioinsQueryErrors.GET_ORGANIZATIONS_ERROR,
          }),
        );
      },
      onSuccess: (data = []) => {
        data.forEach((organization) => {
          queryClient.setQueryData(
            getOrganizationByShortNameQueryKey({ organizationShortName: organization.short_name }),
            organization,
          );
        });
      },
      ...options,
    },
  );
}

export function useOrganizationByShortnameQuery(
  { organizationShortName }: { organizationShortName: string },
  options?: UseApiQueryOptions<typeof userOrganizationService.getOneByShortName>,
) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useQuery(
    getOrganizationByShortNameQueryKey({ organizationShortName }),
    async () =>
      validateApiResponse(await userOrganizationService.getOneByShortName(organizationShortName))
        .data,
    {
      onSuccess: (data) => {
        // set initial organization in organization list
        queryClient.setQueryData(getOrganizationsQueryKey(), (items: unknown[] = []) =>
          items.length === 0 ? [data] : items,
        );
      },
      onError: (error: Error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
}

export function useOrganizationsWithSLA(): UseQueryResult<Organization[] | undefined> {
  return useOrganizationsQuery({
    select: (data = []) =>
      data.filter((organization) => organization.limit_overrides.sla !== 'none'),
  });
}

export function useOrganizationQuery({
  organizationId,
  options,
}: {
  organizationId: string | null;
  options?: UseApiQueryOptions<typeof userOrganizationService.getAll>;
}): UseQueryResult<Organization | undefined> {
  const { data = [], ...query } = useOrganizationsQuery(options);
  const organization = useMemo(() => {
    if (!organizationId && data.length) return data[0];
    return data.find(({ id }) => id === organizationId);
  }, [data, organizationId]);

  const queryWithCurrentOrg = query as UseQueryResult<Organization | undefined>;
  queryWithCurrentOrg.data = organization;
  return queryWithCurrentOrg;
}

/**
 * Pass in 'safe' type so that the returned `data` is always defined.
 * In context of organizations/workspaces we're rendering the loader until the data is fetched.
 * So, allow the user to explicitly tell the compiler that the data will be defined.
 */
export const useCurrentOrganization = <Mode extends RenderMode>(
  options?: UseApiQueryOptions<typeof userOrganizationService.getOneByShortName>,
) => {
  const { organizationShortName } = useParams<'organizationShortName'>();

  return useOrganizationByShortnameQuery(
    { organizationShortName },
    options,
  ) as UseSafeApiQueryResult<Mode, typeof userOrganizationService.getOneByShortName>;
};

/**
 * Should not be used in most cases!
 * Used for cases where organization data is shown outside organization routes, like:
 * - side navigation data while being on user settings
 */
export const useCurrentOrFirstOrganization = <Mode extends RenderMode>(
  options?: UseApiQueryOptions<typeof userOrganizationService.getAll>,
) => {
  const { data = [], ...query } = useOrganizationsQuery(options);
  const { organizationShortName } = useParams();

  const organization = useMemo(() => {
    const selectedOrganizationShortName =
      organizationShortName || window.localStorage.getItem('currentOrganizationShortName');

    if (!selectedOrganizationShortName) return data[0];

    return (
      data.find(({ short_name: shortName }) => shortName === selectedOrganizationShortName) ??
      data[0]
    );
  }, [data, organizationShortName]);

  const queryWithCurrentOrg = query as UseQueryResult<Organization | undefined>;
  queryWithCurrentOrg.data = organization;
  return queryWithCurrentOrg as UseSafeApiQueryResult<
    Mode,
    typeof userOrganizationService.getOneByShortName
  >;
};

export function useCreateOrganizationMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      name,
      email,
      mainBusiness,
      teamCount,
      purpose,
    }: {
      name: string;
      email: string;
      mainBusiness?: string;
      teamCount?: string;
      purpose?: string;
    }) => {
      const response = validateApiResponse(
        await userOrganizationService.create(name, email, mainBusiness, teamCount, purpose),
      );
      return response.data;
    },
    {
      onSuccess: (newOrganization) => {
        const data = queryClient.getQueryState<Organization[]>(getOrganizationsQueryKey())?.data;
        if (!newOrganization) return;
        const nextState = [...(data || []), newOrganization];
        queryClient.setQueryData(getOrganizationsQueryKey(), nextState);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizatioinsQueryErrors.CREATE_ORGANIZATION_ERROR,
          }),
        );
      },
    },
  );
}

export function useEditOrganizationConfigMutation(organization: Organization | undefined) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (update: OrganizationConfiguration) => {
      if (!organization) throw new Error('The organization to be updated is empty');

      const response = validateApiResponse(
        await organizationService.editConfigById(organization.id, update),
      );

      return response.data;
    },
    {
      onSuccess: (updatedData) => {
        queryClient.invalidateQueries(
          getOrganizationByShortNameQueryKey({ organizationShortName: updatedData.short_name }),
        );

        const data = queryClient.getQueryState<Organization[]>(getOrganizationsQueryKey())?.data;
        if (!updatedData || !data) return;

        const updatedIdx = data?.findIndex(({ id }) => id === updatedData?.id);
        if (updatedIdx === -1) return;

        queryClient.setQueryData(getOrganizationsQueryKey(), [
          ...data.slice(0, updatedIdx),
          { ...data[updatedIdx], ...updatedData },
          ...data.slice(updatedIdx + 1),
        ]);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizatioinsQueryErrors.EDIT_ORGANIZATION_ERROR,
          }),
        );
      },
    },
  );
}

export const usePatchOrganizationMutation = <T extends typeof organizationService.editOneById>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      organizationShortName,
      data,
    }: {
      organizationShortName: string;
      data: Partial<ApiQueryFnData<T>>;
    }) => {
      const organizationData: ApiQueryFnData<T> | undefined = queryClient.getQueryData(
        getOrganizationByShortNameQueryKey({ organizationShortName }),
      );

      if (!organizationData) {
        throw new Error('failed to find organization to patch');
      }

      return validateApiResponse(
        await organizationService.editOneById(organizationData.id, {
          ...organizationData,
          ...data,
          details: {
            ...organizationData.details,
            ...data.details,
          },
        }),
      ).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: (updatedData, { organizationShortName }) => {
        queryClient.invalidateQueries(
          getOrganizationByShortNameQueryKey({ organizationShortName }),
        );
        const data = queryClient.getQueryState<Organization[]>(getOrganizationsQueryKey())?.data;
        if (!updatedData || !data) return;

        const updatedIdx = data?.findIndex(({ id }) => id === updatedData?.id);
        if (updatedIdx === -1) return;

        queryClient.setQueryData(getOrganizationsQueryKey(), [
          ...data.slice(0, updatedIdx),
          { ...data[updatedIdx], ...updatedData },
          ...data.slice(updatedIdx + 1),
        ]);
      },
      ...options,
    },
  );
};

export function useEditOrganizationMutation(organization: Organization | undefined) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (update: Partial<Pick<Organization, 'name' | 'short_name' | 'details'>>) => {
      if (!organization) throw new Error('The organization to be updated is empty');

      const response = validateApiResponse(
        await organizationService.editOneById(organization.id, {
          ...organization,
          ...update,
        }),
      );
      return response.data;
    },
    {
      onSuccess: (updatedData) => {
        const data = queryClient.getQueryState<Organization[]>(getOrganizationsQueryKey())?.data;
        if (!updatedData || !data) return;

        const updatedIdx = data?.findIndex(({ id }) => id === updatedData?.id);
        if (updatedIdx === -1) return;

        queryClient.setQueryData(getOrganizationsQueryKey(), [
          ...data.slice(0, updatedIdx),
          { ...data[updatedIdx], ...updatedData },
          ...data.slice(updatedIdx + 1),
        ]);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizatioinsQueryErrors.EDIT_ORGANIZATION_ERROR,
          }),
        );
      },
    },
  );
}

export function useDeleteOrganizationMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (organizationId: string) => {
      const response = validateApiResponse(await organizationService.deleteOneById(organizationId));
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getOrganizationsQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizatioinsQueryErrors.DELETE_ORGANIZATION_ERROR,
          }),
        );
      },
    },
  );
}

export function useLeaveOrganizationMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ organizationId, userId }: { organizationId: string; userId: string }) => {
      const response = validateApiResponse(
        await organizationService.removeOrganizationMember(organizationId, userId),
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getOrganizationsQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizatioinsQueryErrors.LEAVE_ORGANIZATION_ERROR,
          }),
        );
      },
    },
  );
}
